(function () {
  'use strict';

  /* Pure date logic, kept separate and exposed so it can be tested directly in
   * Node against every day of the week without driving a browser. Node and the
   * browser share the same Intl implementation, so the results match.
   *
   * `now`  - a Date (the real clock, or an injected one in tests)
   * `opts`:
   *   tz             - IANA zone of the WAREHOUSE, not the shopper. A London
   *                    visitor must still see the Texas warehouse's day.
   *   days           - comma-separated day numbers, 0=Sun .. 6=Sat.
   *   cutoffHour     - warehouse-local hour after which today is missed (14 = 2pm).
   *   prefixToday    - wording when the order still makes today's run. A
   *                    `{countdown}` token is replaced with the time remaining.
   *   prefixTomorrow - wording when the next ship day is tomorrow.
   *   prefix         - wording for any later ship day.
   *
   * Returns { text, today, msLeft, shipDate }. msLeft is milliseconds to the
   * cutoff and is null unless the order would ship today -- the caller uses it
   * to re-render the countdown and to notice the moment the cutoff passes.
   * shipDate is the UTC-midnight date the order actually leaves on, and is what
   * arrivalRangeText below derives the delivery window from, so the two lines
   * cannot drift apart.
   *
   * THE CUTOFF IS THE POINT OF THIS FUNCTION. Confirmed by the client (Farah,
   * 2026-09-09): orders are processed Monday to Friday up to 2pm, and anything
   * after Friday's 2pm goes out on Monday. Before this the line always said
   * "ships tomorrow", which understated the offer for most of the working day
   * and overstated it every weekend.
   *
   * Public holidays are NOT handled: the line is optimistic on those.
   */
  function humanGap(mins) {
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    if (h && m) return h + ' hr ' + m + ' min';
    if (h) return h + (h === 1 ? ' hour' : ' hours');
    return m + (m === 1 ? ' min' : ' min');
  }

  function nextShipText(now, opts) {
    opts = opts || {};
    var none = { text: '', today: false, msLeft: null };

    var shipDays = String(opts.days == null ? '1,2,3,4,5' : opts.days).split(',')
      .map(function (n) { return parseInt(n, 10); })
      .filter(function (n) { return n >= 0 && n <= 6; });
    if (!shipDays.length) return none;

    // Minutes past warehouse-local midnight at which today's run closes.
    var cutoffHour = parseFloat(opts.cutoffHour);
    if (!(cutoffHour >= 0 && cutoffHour <= 24)) cutoffHour = 14;
    var cutoffMins = Math.round(cutoffHour * 60);

    var today, nowMins;
    try {
      // en-CA formats as YYYY-MM-DD, which parses unambiguously. h23 keeps the
      // hour in 0-23 -- hour12:false yields "24" for midnight in some engines.
      var got = {};
      new Intl.DateTimeFormat('en-CA', {
        timeZone: opts.tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(now).forEach(function (p) { got[p.type] = p.value; });
      today = new Date(Date.UTC(+got.year, +got.month - 1, +got.day));
      nowMins = (+got.hour) * 60 + (+got.minute);
    } catch (e) {
      today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      nowMins = now.getHours() * 60 + now.getMinutes();
    }

    // Today still counts, but only before the cutoff and only on a ship day.
    if (shipDays.indexOf(today.getUTCDay()) !== -1 && nowMins < cutoffMins) {
      var left = cutoffMins - nowMins;
      return {
        text: String(opts.prefixToday || '').replace('{countdown}', humanGap(left)),
        today: true,
        msLeft: left * 60000,
        shipDate: today
      };
    }

    for (var i = 1; i <= 7; i++) {
      var d = new Date(today.getTime() + i * 86400000);
      if (shipDays.indexOf(d.getUTCDay()) === -1) continue;
      var when = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric'
      }).format(d);
      return {
        text: (i === 1 ? opts.prefixTomorrow : opts.prefix) + ' ' + when,
        today: false,
        msLeft: null,
        shipDate: d
      };
    }
    return none;
  }

  /* Delivery window, derived from the date the order actually LEAVES.
   *
   * `shipDate` - the UTC-midnight Date nextShipText resolved to.
   * `minDays` / `maxDays` - transit time in BUSINESS days (client: 2-4).
   *
   * Why a range and never a single date: the client's words were "it can vary,
   * 2-4 business days on average". There is no carrier integration here, so a
   * single date would be arithmetic on an average presented as a commitment --
   * and every miss is a support ticket. A range keeps the concreteness that
   * makes dates outperform durations without inventing a guarantee. CLAUDE.md
   * records this; do not "simplify" it to one date.
   *
   * Transit skips weekends (carriers do not deliver Sat/Sun on ground service),
   * and public holidays are NOT handled -- optimistic on those, same caveat as
   * the ship date itself.
   *
   * Returns { text, from, to } or null when it cannot be computed.
   */
  function addBusinessDays(from, n) {
    var d = new Date(from.getTime());
    var added = 0;
    while (added < n) {
      d = new Date(d.getTime() + 86400000);
      var wd = d.getUTCDay();
      if (wd !== 0 && wd !== 6) added++;      // 0=Sun, 6=Sat
    }
    return d;
  }

  function arrivalRangeText(shipDate, minDays, maxDays, prefix) {
    if (!shipDate) return null;
    var lo = parseInt(minDays, 10);
    var hi = parseInt(maxDays, 10);
    if (!(lo >= 1) || !(hi >= lo)) return null;

    var from = addBusinessDays(shipDate, lo);
    var to   = addBusinessDays(shipDate, hi);

    var md = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', month: 'short', day: 'numeric'
    });
    // En dash, not a hyphen: this is a range, and "Oct 6 - Oct 8" reads as a
    // subtraction at small sizes.
    var span = md.format(from) + ' – ' + md.format(to);
    return {
      text: String(prefix || 'Most orders arrive {range}').replace('{range}', span),
      from: from,
      to: to
    };
  }

  window.ShowerSmartPDP = window.ShowerSmartPDP || {};
  window.ShowerSmartPDP.nextShipText = nextShipText;
  window.ShowerSmartPDP.arrivalRangeText = arrivalRangeText;
})();

/* ShowerSMART v2 PDP — sticky add-to-cart bar.
 *
 * Deliberately small. The sticky button carries a `form=` attribute pointing at
 * the real product form, so pressing it fires that form's own submit event and
 * the theme's <product-form> element handles the cart add. No cart logic lives
 * here — we only react to the theme's own completion event.
 *
 * Responsibilities:
 *   1. Show the bar only once the hero CTA has left the viewport (IntersectionObserver).
 *   2. Mirror the live price, saving and sold-out state from the buy box.
 *   3. Send the shopper to the cart page once the theme reports the add is done.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.ssInit === 'true') return;
    root.dataset.ssInit = 'true';

    var sectionId = root.dataset.sectionId;
    var bar = root.querySelector('[data-ss-sticky]');
    var heroCta = root.querySelector('[data-ss-hero-cta]');
    if (!bar || !heroCta) return;

    /* --- 1. Visibility, driven by the hero CTA, not a pixel offset --- */
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // "Not intersecting" is true both when the button is above the
            // viewport AND when it is still below it, so on a long buy box the
            // bar appeared immediately on load. Only show it once the button
            // has actually scrolled PAST the top of the screen.
            var scrolledPast = entry.boundingClientRect.top < 0;
            var show = !entry.isIntersecting && scrolledPast;
            bar.dataset.visible = show ? 'true' : 'false';
            // Pad the page while the bar is up so it can never sit on top of
            // the footer's links and form fields (CLAUDE.md accessibility rule).
            document.body.classList.toggle('ss-has-sticky', show);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0 }
      );
      observer.observe(heroCta);

      // IntersectionObserver only fires when the intersecting state CHANGES.
      // An instant jump (a back-to-top link, an in-page anchor) takes the button
      // from above the viewport straight to below it without ever intersecting,
      // so no callback runs and the bar stays stuck visible. Correct it from the
      // live rect on scroll; rAF-throttled, one measurement per frame.
      var stickyTick = false;
      var refreshSticky = function () {
        stickyTick = false;
        var show = heroCta.getBoundingClientRect().bottom < 0;
        if ((bar.dataset.visible === 'true') === show) return;
        bar.dataset.visible = show ? 'true' : 'false';
        document.body.classList.toggle('ss-has-sticky', show);
      };
      window.addEventListener('scroll', function () {
        if (stickyTick) return;
        stickyTick = true;
        window.requestAnimationFrame(refreshSticky);
      }, { passive: true });
    } else {
      // No IntersectionObserver: leave the bar hidden rather than pin it open.
      bar.dataset.visible = 'false';
    }

    /* --- 1b. Keep ?view= alive across variant changes ---
     * The theme's VariantSelects.updateURL does:
     *   history.replaceState({}, '', `${dataset.url}?variant=${id}`)
     * which rebuilds the query string from scratch and therefore DROPS ?view=.
     * Re-apply the param afterwards.
     *
     * Since the 2026-09-02 cutover this page is served at the bare canonical
     * /products/shower-smart, so no shopper carries a ?view= param and the guard
     * below means this never registers a listener for them. Keep it anyway: it is
     * what makes QA possible. On an unpublished or duplicated theme, where the
     * product is still assigned to the Replo template, ?view=v2 is the ONLY way
     * to reach this page -- and without this handler the first variant click
     * would bounce the tester onto the Replo template mid-checklist.
     */
    var initialView = new URLSearchParams(window.location.search).get('view');
    if (initialView) {
      root.addEventListener('change', function () {
        // Run after the theme's own change handler has rewritten the URL.
        window.setTimeout(function () {
          var params = new URLSearchParams(window.location.search);
          if (params.get('view') === initialView) return;
          params.set('view', initialView);
          window.history.replaceState(
            {}, '', window.location.pathname + '?' + params.toString() + window.location.hash
          );
        }, 0);
      });
    }

    /* --- 1c. Marquee: make room for it above the menu -----------------------
     * The banner is position:absolute at the top of the DOCUMENT so it sits
     * above the theme header and then scrolls away. Absolute means it is out of
     * flow, so the header would start underneath it without this: body
     * padding-top reserves exactly the bar's height.
     *
     * MEASURED, never assumed. The height moves with the theme's font scale and
     * with how the three items wrap on a narrow screen, and a hardcoded value
     * desyncs silently -- leaving either a gap above the logo or the logo
     * tucked under the bar.
     *
     * Deliberately much smaller than the version this replaced: that one was
     * position:fixed, so it also had to push the header down for as long as the
     * header was fixed. Absolute needs none of that -- by the time the header
     * goes fixed, the bar has scrolled off.
     */
    var marquee = root.querySelector('.ss-marquee');
    if (marquee) {
      var applyMarqueeOffset = function () {
        var h = Math.round(marquee.getBoundingClientRect().height);
        if (h > 0) document.body.style.paddingTop = h + 'px';
      };
      applyMarqueeOffset();
      window.addEventListener('resize', applyMarqueeOffset, { passive: true });
      // Webfonts land after first paint and change the line box, so re-measure
      // once they are ready rather than baking in the fallback font's height.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(applyMarqueeOffset).catch(function () {});
      }
    }

    /* --- 1d. Hero carousel: dots + variant image sync ------------------------ */
    var track = root.querySelector('[data-ss-gallery-track]');
    var dots = [].slice.call(root.querySelectorAll('[data-ss-dot]'));
    if (track && dots.length) {
      var slides = [].slice.call(track.querySelectorAll('[data-ss-slide]'));

      var goTo = function (i, smooth) {
        var slide = slides[i];
        if (!slide) return;
        // Slides use scroll-snap-align:center and are narrower than the track
        // (they peek), so the snap position is the CENTRED offset, not
        // offsetLeft. Using offsetLeft leaves it (trackW - slideW)/2 short and
        // the carousel settles mid-image.
        var centred = (slide.offsetLeft - track.offsetLeft)
                    - (track.clientWidth - slide.offsetWidth) / 2;
        var max = track.scrollWidth - track.clientWidth;
        track.scrollTo({ left: Math.max(0, Math.min(max, Math.round(centred))),
                         behavior: smooth === false ? 'auto' : 'smooth' });
      };

      /* Thumbnails are a DESKTOP enhancement over the same slide set, so they
       * hook the existing goTo/markActive rather than tracking their own idea of
       * the current slide. Everything that already calls markActive -- dots,
       * arrows, the track's scroll listener, the variant-change handler -- keeps
       * them in sync for free.
       *
       * data-ss-thumb carries the SLIDE index, which is why the Liquid emits
       * thumbnails in slide order rather than product.media order. */
      var thumbs = [].slice.call(root.querySelectorAll('[data-ss-thumb]'));

      var markActive = function (i) {
        dots.forEach(function (d, n) {
          d.setAttribute('aria-current', n === i ? 'true' : 'false');
        });
        thumbs.forEach(function (t, n) {
          t.setAttribute('aria-selected', n === i ? 'true' : 'false');
        });
        // Keep the active thumbnail in view: only six of thirteen fit, so
        // swiping the carousel would otherwise leave the highlight off-screen.
        // `nearest` scrolls the strip the minimum needed and, because the strip
        // is the only scrollable ancestor, does not move the page.
        var active = thumbs[i];
        if (active && active.scrollIntoView) {
          active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      };

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { goTo(i); markActive(i); });
      });

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          goTo(i); markActive(i); syncArrows(i);
        });
      });

      /* --- Thumbnail strip: arrows + drag ------------------------------------
       * The scrollbar is hidden by request, so these are the only affordances
       * for reaching thumbnails 6-13. Both move the STRIP; neither changes
       * which slide is showing -- that is what clicking a thumbnail is for.
       */
      var strip = root.querySelector('[data-ss-thumbs]');
      if (strip) {
        var tPrev = root.querySelector('[data-ss-thumbs-prev]');
        var tNext = root.querySelector('[data-ss-thumbs-next]');

        var syncStripArrows = function () {
          var max = strip.scrollWidth - strip.clientWidth;
          /* The resting scrollLeft at the start is NOT 0 -- scroll-snap settles
           * the first thumbnail against the strip's own left padding, so it sits
           * at ~3px and a 1px tolerance left the left arrow permanently enabled
           * (caught in testing 2026-10-01). Derive the slack from the padding
           * rather than hardcoding it, so changing the padding cannot re-break
           * this. +2 absorbs fractional layout widths at either end. */
          var cs = getComputedStyle(strip);
          var slack = (parseFloat(cs.paddingLeft) || 0) + 2;
          if (tPrev) tPrev.disabled = strip.scrollLeft <= slack;
          if (tNext) tNext.disabled = strip.scrollLeft >= max - slack;
        };

        // Scroll by a whole "page" of thumbnails less one, so the thumbnail at
        // the edge stays visible and the shopper keeps their place.
        var stripStep = function (dir) {
          var first = strip.querySelector('.ss-thumb');
          var w = first ? first.getBoundingClientRect().width + 8 : 116;
          var page = Math.max(w, strip.clientWidth - w);
          strip.scrollBy({ left: dir * page, behavior: 'smooth' });
        };

        if (tPrev) tPrev.addEventListener('click', function () { stripStep(-1); });
        if (tNext) tNext.addEventListener('click', function () { stripStep(1); });
        strip.addEventListener('scroll', syncStripArrows, { passive: true });
        window.addEventListener('resize', syncStripArrows, { passive: true });
        syncStripArrows();

        /* Drag. Mirrors the carousel's handler, including the touch skip: a
         * mouse drag never scrolls an overflow container (browser behaviour,
         * not a bug), while touch already does it natively and better. */
        /* THE DRAG MUST NOT START ON pointerdown. Capturing the pointer, or
         * setting the dragging flag, on every press broke clicking a thumbnail
         * entirely -- and silently:
         *
         *   - setPointerCapture retargets the follow-up `click` to the strip, so
         *     the button's own click listener never fires; and
         *   - data-dragging applies pointer-events:none to the thumbnails, which
         *     removes them as the click target as well.
         *
         * Both only bite a REAL click. A synthetic pointer sequence followed by
         * el.click() skips the retarget entirely, which is why the first version
         * of this passed its test and still failed for Joe (2026-10-01). Test
         * this with a real click, never a dispatched one.
         *
         * So: record the press, arm nothing. Promote to a drag only once the
         * pointer has actually moved past the threshold. A plain click never
         * reaches that line and is left completely untouched.
         */
        var DRAG_THRESHOLD = 5;   // px; below this a press is a click, not a drag
        var tDrag = null;

        strip.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'touch') return;
          if (e.button !== 0) return;
          tDrag = {
            startX: e.clientX,
            startLeft: strip.scrollLeft,
            moved: 0,
            active: false,          // becomes true only past the threshold
            pointerId: e.pointerId
          };
        });

        strip.addEventListener('pointermove', function (e) {
          if (!tDrag) return;
          var dx = e.clientX - tDrag.startX;
          if (Math.abs(dx) > tDrag.moved) tDrag.moved = Math.abs(dx);

          if (!tDrag.active) {
            if (tDrag.moved <= DRAG_THRESHOLD) return;   // still a click
            tDrag.active = true;
            strip.dataset.dragging = 'true';
            try { strip.setPointerCapture(e.pointerId); } catch (err) {}
          }
          strip.scrollLeft = tDrag.startLeft - dx;
        });

        var endThumbDrag = function (e) {
          if (!tDrag) return;
          // Only a promoted drag swallows the click. A press that never passed
          // the threshold leaves the click alone, which is what makes selecting
          // a thumbnail work at all.
          var dragged = tDrag.active;
          tDrag = null;
          if (dragged) {
            delete strip.dataset.dragging;
            try { strip.releasePointerCapture(e.pointerId); } catch (err) {}
          }
          syncStripArrows();
          // A drag that ends over a thumbnail must not also select it. Swallow
          // exactly one click, in the capture phase, before it reaches the
          // button -- without this, every drag jumps the carousel.
          if (dragged) {
            var swallow = function (ev) {
              ev.stopPropagation(); ev.preventDefault();
              strip.removeEventListener('click', swallow, true);
            };
            strip.addEventListener('click', swallow, true);
            window.setTimeout(function () {
              strip.removeEventListener('click', swallow, true);
            }, 0);
          }
        };
        strip.addEventListener('pointerup', endThumbDrag);
        strip.addEventListener('pointercancel', endThumbDrag);
      }

      // Arrows: a mouse cannot swipe, so without these the dots are the only
      // way to advance on desktop and in browser device emulation.
      var prevBtn = root.querySelector('[data-ss-gallery-prev]');
      var nextBtn = root.querySelector('[data-ss-gallery-next]');
      var currentIndex = function () {
        var mid = track.scrollLeft + track.clientWidth / 2, best = Infinity, n = 0;
        slides.forEach(function (sl, k) {
          var c = sl.offsetLeft - track.offsetLeft + sl.clientWidth / 2;
          var d = Math.abs(c - mid);
          if (d < best) { best = d; n = k; }
        });
        return n;
      };
      var step = function (delta) {
        var i = Math.min(slides.length - 1, Math.max(0, currentIndex() + delta));
        goTo(i); markActive(i); syncArrows(i);
      };
      var syncArrows = function (i) {
        if (prevBtn) prevBtn.disabled = i <= 0;
        if (nextBtn) nextBtn.disabled = i >= slides.length - 1;
      };
      if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
      syncArrows(0);

      /* Drag-to-scroll for mouse and pen.
       *
       * A mouse drag never scrolls an overflow container -- that is browser
       * behaviour, not a bug -- so on desktop and in device emulation the
       * carousel felt broken even though real touch swiping works natively.
       * Fill only that gap: touch is skipped so it keeps native momentum
       * scrolling, which is smoother than anything reimplemented here.
       */
      var drag = null;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;      // native touch scrolling wins
        if (e.button !== 0) return;                 // primary button only
        // The gallery can contain a <video> and its play button. Starting a drag
        // on either would steal the pointer, making play/scrub unusable.
        // (A drag that merely ENDS over the button is still swallowed by the
        // capture-phase click handler in endDrag, so this cannot cause an
        // accidental play.)
        if (e.target && e.target.closest &&
            e.target.closest('video, [data-ss-play]')) return;
        drag = { startX: e.clientX, startLeft: track.scrollLeft, moved: 0 };
        track.dataset.dragging = 'true';            // also disables snap while dragging
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      });

      track.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.startX;
        drag.moved = Math.max(drag.moved, Math.abs(dx));
        track.scrollLeft = drag.startLeft - dx;
        e.preventDefault();
      });

      var endDrag = function (e) {
        if (!drag) return;
        var wasDrag = drag.moved > 5;
        drag = null;
        delete track.dataset.dragging;              // restores scroll-snap
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
        // Settle on the nearest slide, since snapping was off during the drag.
        var i = currentIndex();
        goTo(i); markActive(i); syncArrows(i);
        // Swallow the click that follows a real drag so releasing over a dot
        // or the image does not activate anything.
        if (wasDrag) {
          track.addEventListener('click', function swallow(ev) {
            ev.stopPropagation(); ev.preventDefault();
            track.removeEventListener('click', swallow, true);
          }, true);
        }
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });

      // Keep the dots honest when the shopper swipes rather than taps.
      var scrollTick = false;
      track.addEventListener('scroll', function () {
        if (scrollTick) return;
        scrollTick = true;
        window.requestAnimationFrame(function () {
          scrollTick = false;
          var mid = track.scrollLeft + track.clientWidth / 2;
          var nearest = 0, best = Infinity;
          slides.forEach(function (sl, n) {
            var c = sl.offsetLeft - track.offsetLeft + sl.clientWidth / 2;
            var d = Math.abs(c - mid);
            if (d < best) { best = d; nearest = n; }
          });
          markActive(nearest);
          if (typeof syncArrows === 'function') syncArrows(nearest);
        });
      }, { passive: true });

      // Variant -> image. The variant JSON is already embedded in
      // <variant-radios> for the theme's own use, so no new data source.
      var radios = root.querySelector('variant-radios');
      var variantData = null;
      if (radios) {
        try {
          variantData = JSON.parse(radios.querySelector('[type="application/json"]').textContent);
        } catch (e) { variantData = null; }
      }

      if (variantData) {
        root.addEventListener('change', function () {
          window.setTimeout(function () {
            var id = parseInt(new URLSearchParams(window.location.search).get('variant'), 10);
            if (!id) return;
            var v = variantData.filter(function (x) { return x.id === id; })[0];
            if (!v) return;
            // featured_image.id and featured_media.id are DIFFERENT id spaces.
            // Slides are now built from product.MEDIA, so match featured_media
            // FIRST; featured_image is only a fallback for image-built markup.
            // If the Liquid ever goes back to product.images, flip this back --
            // a mismatch here fails silently, with the carousel simply refusing
            // to follow the swatch.
            var mediaId = (v.featured_media && v.featured_media.id) ||
                          (v.featured_image && v.featured_image.id);
            if (!mediaId) return;
            for (var n = 0; n < slides.length; n++) {
              if (String(slides[n].dataset.mediaId) === String(mediaId)) {
                goTo(n); markActive(n); break;
              }
            }
          }, 60);
        });
      }
    }

    /* --- 1e. Selected-option label ("Color  Black") --------------------------
     * The markup carried a data-ss-selected-for hook from the start but nothing
     * ever wrote to it, so the value beside the option name never changed.
     * Generic: works for any option group rendered with a value label.
     */
    var valueLabels = [].slice.call(root.querySelectorAll('[data-ss-selected-for]'));
    if (valueLabels.length) {
      var syncOptionLabels = function () {
        valueLabels.forEach(function (el) {
          var group = root.querySelectorAll('.ss-optgroup')[parseInt(el.dataset.ssSelectedFor, 10)];
          if (!group) return;
          var checked = group.querySelector('input[type="radio"]:checked');
          if (checked) el.textContent = checked.value;
        });
      };
      syncOptionLabels();
      root.addEventListener('change', syncOptionLabels);
    }

    /* --- 1f. Ship-date line --------------------------------------------------
     * Computed from the WAREHOUSE timezone, deliberately:
     *   - the visitor's raw clock would show the wrong day abroad;
     *   - Liquid would be baked into Shopify's CDN cache and go stale.
     * Intl gives us "today" in a fixed zone on every page load.
     *
     * Does NOT account for public holidays -- the line is optimistic on those.
     */
    var shipEl = root.querySelector('[data-ss-ship]');
    if (shipEl) {
      var out = shipEl.querySelector('[data-ss-ship-text]');
      var shipOpts = {
        tz: shipEl.dataset.shipTz || 'America/Chicago',
        days: shipEl.dataset.shipDays || '1,2,3,4,5',
        cutoffHour: shipEl.dataset.shipCutoff,
        prefixToday: shipEl.dataset.shipPrefixToday || 'Order in the next {countdown} and it ships today',
        prefixTomorrow: shipEl.dataset.shipPrefixTomorrow || 'Ships tomorrow,',
        prefix: shipEl.dataset.shipPrefix || 'Ships'
      };

      /* Re-rendered on a timer, not once at load.
       *
       * The before-cutoff wording contains a live countdown, so a value written
       * once is wrong within a minute and badly wrong on a page left open. The
       * same tick is what flips "ships today" over to "ships tomorrow" at 2pm
       * without a reload -- a stale "ships today" at 2:05pm is a promise the
       * warehouse cannot keep.
       *
       * 30s rather than 60s so the displayed minute is never more than half a
       * minute behind. Nothing else on the page runs on an interval; this one
       * writes a single text node and only when the string actually changes.
       */
      // The arrival window is derived from the SAME resolved ship date, so the
      // two lines can never disagree -- when the 2pm cutoff passes and the ship
      // date rolls forward, the delivery window rolls with it on the same tick.
      var arriveEl = root.querySelector('[data-ss-arrive-text]');
      var arriveWrap = root.querySelector('[data-ss-arrive]');

      var shipTick = function () {
        var r = window.ShowerSmartPDP.nextShipText(new Date(), shipOpts);
        if (out && r.text && out.textContent !== r.text) out.textContent = r.text;
        shipEl.dataset.shipsToday = r.today ? 'true' : 'false';

        if (arriveEl && arriveWrap) {
          var a = window.ShowerSmartPDP.arrivalRangeText(
            r.shipDate,
            arriveWrap.dataset.transitMin,
            arriveWrap.dataset.transitMax,
            arriveWrap.dataset.arrivePrefix
          );
          // Hide the line rather than show a half-computed one: a blank or
          // wrong delivery estimate is worse than no estimate.
          if (a && a.text) {
            if (arriveEl.textContent !== a.text) arriveEl.textContent = a.text;
            arriveWrap.hidden = false;
          } else {
            arriveWrap.hidden = true;
          }
        }
      };
      shipTick();
      setInterval(shipTick, 30000);
    }

    /* --- 2. Mirror price --- */
    var sourcePrice = document.getElementById('price-' + sectionId);
    var mirrorPrice = bar.querySelector('[data-ss-sticky-price]');

    // querySelectorAll, not querySelector: the closing offer block repeats the
    // buy button, so there is more than one of these. While this was singular
    // only the hero button updated on a variant change and the closing one sat
    // showing a stale price.
    var btnPrices = root.querySelectorAll('[data-ss-btn-price]');

    function syncPrice() {
      if (!sourcePrice) return;
      // Copy the node first and drop screen-reader-only text, otherwise the
      // bar reads "Regular price $79.99" instead of "$79.99".
      var clone = sourcePrice.cloneNode(true);
      clone.querySelectorAll('.ss-visually-hidden').forEach(function (el) {
        el.parentNode.removeChild(el);
      });
      var text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return;
      if (mirrorPrice) mirrorPrice.textContent = text;
      btnPrices.forEach(function (el) { el.textContent = text; });
    }

    /* --- 2b. Saving percentage inside the Add to cart button ---------------
     * The percentages are computed in Liquid (see "Bundle saving maths" in
     * showersmart-pdp.liquid) and handed over as a variant-id -> percent map.
     * JS never does the arithmetic: one derivation, server-side, so the button
     * and the pack badge can never disagree, and a price change in Shopify is
     * picked up automatically.
     *
     * The span is emptied rather than removed when a variant has no saving --
     * re-creating the node would race the theme's own rewrite of this button.
     */
    // Plural for the same reason as btnPrices above.
    var btnSaves = root.querySelectorAll('[data-ss-btn-save]');
    var savings = null;
    try {
      savings = JSON.parse(root.dataset.ssSavings || '{}');
    } catch (e) {
      savings = {};
    }
    var saveFormat = root.dataset.ssSaveFormat || 'Save {pct}%';

    function currentVariantId() {
      // The theme keeps the posted variant in the form's hidden id input, and
      // rewrites it on every variant change -- more reliable than re-reading
      // the URL, which lags behind on the first selection.
      var input = form ? form.querySelector('[name="id"]') : null;
      return input ? String(input.value || '') : '';
    }

    function syncSaving() {
      if (!btnSaves.length || !savings) return;
      var pct = savings[currentVariantId()];
      var text = pct > 0 ? saveFormat.replace('{pct}', pct) : '';
      btnSaves.forEach(function (el) { el.textContent = text; });
    }

    /* --- 3. Mirror availability from the real add button ------------------
     * Both the sticky bar and the closing offer button live OUTSIDE the product
     * form (they reach it with form="..."). The theme's toggleAddButton does
     *   productForm.querySelector('[name="add"] > span')
     * so it never touches either of them. Without this, a sold-out variant
     * would leave them reading "Get my shower organized" while the real button
     * says "Sold out" -- and the closing one would still look clickable.
     *
     * Mirroring the label also keeps the closing button's wording identical to
     * the hero's for free, which is what Joe asked for on 2026-09-02.
     */
    var form = document.getElementById('product-form-' + sectionId);
    var sourceBtn = form ? form.querySelector('[name="add"]') : null;
    var mirrorBtns = [];
    var stickyBtn = bar.querySelector('[data-ss-sticky-add]');
    if (stickyBtn) mirrorBtns.push(stickyBtn);
    root.querySelectorAll('[data-ss-mirror-add]').forEach(function (el) { mirrorBtns.push(el); });

    // Each mirror keeps its OWN server-rendered label and gets it back whenever
    // the variant is available. This matters for the sticky bar, which is
    // deliberately shorter than the hero button ("Add to cart" vs "Get my
    // shower organized") because it shares one mobile-width row with the title
    // and price. Blindly copying the source label would have silently undone
    // that the moment this ran.
    var labelled = mirrorBtns.slice();
    if (sourceBtn) labelled.push(sourceBtn);
    labelled.forEach(function (btn) {
      var own = btn.querySelector('span');
      if (own) btn.dataset.ssOwnLabel = own.textContent.trim();
    });

    /* The real button needs its label restored too, not just the mirrors.
     *
     * The theme's toggleAddButton does
     *   productForm.querySelector('[name="add"] > span').textContent = <its own string>
     * on EVERY variant change, so the configured CTA copy ("Get my shower
     * organized") reverted to the theme's "Add to cart" the moment a shopper
     * picked a pack. That had been happening since the label was customised and
     * is invisible until you actually switch a variant -- caught in testing
     * 2026-09-02.
     *
     * Only write when the text actually differs. The MutationObserver below
     * watches this button's subtree, so an unconditional write would retrigger
     * itself forever.
     */
    /* Buttons marked data-ss-cta-variable take their wording from the
     * variant-id -> label map instead of their own server-rendered label, so
     * "Get my shower organized" becomes "Get my showers organized" the moment a
     * shopper picks the 2-pack (Joe, 2026-09-09).
     *
     * Everything else keeps ssOwnLabel: the sticky bar says "Add to cart"
     * because it shares one mobile row with the title and price, and a final
     * offer block with an explicit per-block label has deliberately overridden
     * the wording. Neither should be rewritten here.
     */
    var labels = null;
    try {
      labels = JSON.parse(root.dataset.ssLabels || '{}');
    } catch (e) {
      labels = {};
    }

    /* Pack value for the sticky bar, e.g. "2-Pack". Without it the bar reads
     * "ShowerSMART / $139.99" and a shopper who chose the 2-pack can read that
     * as the unit price (Joe, 2026-10-20). Mirrored on variant change like the
     * price and the saving, from a map derived server-side -- JS never works out
     * which option is the pack. */
    var packs = null;
    try {
      packs = JSON.parse(root.dataset.ssPacks || '{}');
    } catch (e) {
      packs = {};
    }
    var stickyPacks = root.querySelectorAll('[data-ss-sticky-pack]');

    function syncPack() {
      if (!stickyPacks.length) return;
      var txt = packs[currentVariantId()] || '';
      stickyPacks.forEach(function (el) {
        if (el.textContent !== txt) el.textContent = txt;
        // Empty for a product with no pack option; hidden so its separator goes too.
        el.hidden = txt === '';
      });
    }

    function applyLabel(btn, disabled, soldOutText) {
      var span = btn.querySelector('span');   // first span: price/saving are later
      if (!span) return;
      var own = btn.dataset.ssOwnLabel || span.textContent;
      if (btn.hasAttribute('data-ss-cta-variable')) {
        // Fall back to the button's own label when the variant is not in the
        // map, so an unexpected id can never blank the button.
        own = labels[currentVariantId()] || own;
      }
      var want = disabled ? soldOutText : own;
      if (want && span.textContent.trim() !== want) span.textContent = want;
    }

    function syncAvailability() {
      if (!sourceBtn) return;
      var disabled = sourceBtn.hasAttribute('disabled');
      // When sold out the theme has already written its own wording into the
      // source button; reuse it so we never invent a second sold-out string.
      var srcSpan = sourceBtn.querySelector('span');
      var soldOutText = srcSpan ? srcSpan.textContent.trim() : '';

      mirrorBtns.forEach(function (btn) {
        if (disabled) {
          btn.setAttribute('disabled', 'disabled');
        } else {
          btn.removeAttribute('disabled');
        }
        applyLabel(btn, disabled, soldOutText);
      });

      applyLabel(sourceBtn, disabled, soldOutText);
    }

    syncPrice();
    syncPack();
    syncAvailability();
    // Runs after `form` is assigned above -- syncSaving reads the form's hidden
    // id input, so calling it any earlier would find nothing.
    syncSaving();

    // The theme swaps #price-<id> wholesale on variant change and toggles the
    // add button's disabled attribute, so watch both rather than guessing when.
    if ('MutationObserver' in window) {
      if (sourcePrice) {
        new MutationObserver(function () {
          syncPrice();
          // The price node is swapped on every variant change, which makes it
          // the most reliable signal that the saving needs recomputing too.
          syncSaving();
          syncPack();
          // ...and the label, which is now variant-dependent too. The button's
          // own observer below only fires when `disabled` actually changes, so
          // switching between two IN-STOCK packs never reached it -- that is
          // exactly the 1-pack -> 2-pack case the plural label exists for.
          syncAvailability();
        }).observe(sourcePrice, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
      if (sourceBtn) {
        new MutationObserver(syncAvailability).observe(sourceBtn, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true
        });
      }
      // The hidden id input is what we actually read, and on a colour-only
      // change (same price) the price node may not mutate at all.
      var idInput = form ? form.querySelector('[name="id"]') : null;
      if (idInput) {
        new MutationObserver(function () {
          syncSaving();
          syncPack();
          // The posted variant is the key the label map is read by, so re-apply
          // whenever it changes. Both writes are guarded on the value actually
          // differing, so this cannot loop against its own observer.
          syncAvailability();
        }).observe(idInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }
    // Belt and braces: the theme sets .value as a property on some paths, which
    // does not fire a mutation record.
    if (form) form.addEventListener('change', function () { window.setTimeout(syncSaving, 0); });

    /* --- 4. After Add to cart: nothing. Deliberately. --------------------
     * The theme's product-form.js adds via the cart API and opens the drawer.
     * We do not interfere.
     *
     * A previous build redirected to /cart here so shoppers would see the
     * stock/shipping strip. That was reverted 2026-09-02: a full page load
     * between adding and checking out is the most avoidable drop-off on a page
     * that is 95% mobile and converts at 0.06%. The strip moved into the drawer
     * instead (snippets/showersmart-cart-checks.liquid), so nothing was lost.
     *
     * Do not re-add a redirect here. See CLAUDE.md.
     */
  }

  /* --- Live Yotpo star rating, swapped in over the static figures ---------
     The section server-renders verified stars + count so the fold is correct
     immediately, with JS off, and for crawlers. Yotpo's star widget (instance
     744670) mounts in the same slot and we hide the static layer only once the
     widget has actually PAINTED -- not merely when its script has run. If Yotpo
     is slow, blocked, or errors, the static figures simply stay, which is the
     right failure mode: a correct number beats an empty space.

     Height is reserved in CSS, so the swap cannot shift the fold.

     Checking painted-ness rather than existence matters: the widget div is in
     the DOM from the first byte and stays empty until Yotpo fills it. Hiding
     the fallback on existence alone would blank the rating on every slow
     connection -- which is most of the 95% mobile traffic this page is for. */
  /* --- Play overlays -------------------------------------------------------
   * Any element carrying data-ss-video holds a <video> and a decorative play
   * badge. Hide the badge once the video is genuinely playing, bring it back on
   * pause or end.
   *
   * Driven by the video's OWN events rather than a click handler, so it stays
   * correct however playback started -- the native control, a keyboard Space,
   * or the OS media keys. Scoped to data-ss-video rather than the gallery, so
   * the hero carousel and the install video share one implementation.
   */
  function initVideoOverlays(root) {
    [].slice.call(root.querySelectorAll('[data-ss-video]')).forEach(function (wrap) {
      if (wrap.dataset.ssVideoInit === 'true') return;
      var vid = wrap.querySelector('video');
      if (!vid) return;
      wrap.dataset.ssVideoInit = 'true';
      var set = function (playing) { wrap.dataset.ssPlaying = playing ? 'true' : 'false'; };

      /* The badge is a real button and must actually start playback. Clicks
       * cannot be left to fall through to the <video>: a video ignores clicks
       * on its body and only responds to its own control bar, so the most
       * obvious target on screen did nothing at all. */
      var btn = wrap.querySelector('[data-ss-play]');
      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var p = vid.play();
          // Safari returns undefined; Chrome rejects if the gesture is refused.
          if (p && typeof p.catch === 'function') {
            p.catch(function () { vid.controls = true; });
          }
        });
      }

      vid.addEventListener('play', function () { set(true); });
      vid.addEventListener('playing', function () { set(true); });
      vid.addEventListener('pause', function () { set(false); });
      vid.addEventListener('ended', function () { set(false); });
    });
  }

  function initRating(root) {
    var slot = root.querySelector('[data-ss-rating]');
    if (!slot || slot.dataset.ssRatingInit === 'true') return;
    var widget = slot.querySelector('[data-ss-rating-widget]');
    if (!widget) return;
    slot.dataset.ssRatingInit = 'true';

    var stop;
    function painted() {
      // offsetHeight covers the shadow-DOM case, where childElementCount is 0.
      return widget.offsetHeight > 0 || widget.childElementCount > 0;
    }
    function check() {
      if (!painted()) return false;
      slot.classList.add('is-live');
      if (stop) stop();
      return true;
    }

    if (check()) return;

    var mo = new MutationObserver(check);
    mo.observe(widget, { childList: true, subtree: true });
    // Yotpo can paint into a shadow root, which MutationObserver will not see,
    // so poll alongside it. Both are cleared by the same stop().
    var poll = setInterval(check, 400);
    var giveUp = setTimeout(function () { if (stop) stop(); }, 15000);
    stop = function () {
      mo.disconnect();
      clearInterval(poll);
      clearTimeout(giveUp);
      stop = null;
    };
  }

  function boot() {
    document.querySelectorAll('[data-ss-pdp]').forEach(init);
    document.querySelectorAll('[data-ss-pdp]').forEach(initRating);
    document.querySelectorAll('[data-ss-pdp]').forEach(initVideoOverlays);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
